import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { getVideoById, getRelatedVideos } from '@/mocks/videos';
import { getCategoryBySlug } from '@/mocks/categories';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const video = id ? getVideoById(id) : undefined;

  if (!video) {
    return <NotFound />;
  }

  const category = getCategoryBySlug(video.category);
  const related = getRelatedVideos(video.id, video.category);

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: video.title,
        description: video.description,
        author: { '@type': 'Person', name: video.author },
        datePublished: video.date,
        dateModified: video.date,
        image: video.thumbnail,
        articleBody: video.content.replace(/<[^>]*>/g, ''),
        keywords: video.tags.join(','),
        publisher: {
          '@type': 'Organization',
          name: '精品视频精选平台',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://www.jingpinsp.com/article/${video.id}`,
        },
      },
      {
        '@type': 'VideoObject',
        name: video.title,
        description: video.description,
        thumbnailUrl: video.thumbnail,
        uploadDate: video.date,
        duration: video.duration,
        contentUrl: `https://www.jingpinsp.com/article/${video.id}`,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.jingpinsp.com' },
          { '@type': 'ListItem', position: 2, name: category.name, item: `https://www.jingpinsp.com/category/${video.category}` },
          { '@type': 'ListItem', position: 3, name: video.title },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${video.title} - 精品视频精选平台`}
        description={video.description}
        keywords={video.tags.join(',')}
        canonicalUrl={`https://www.jingpinsp.com/article/${video.id}`}
        ogTitle={video.title}
        ogDescription={video.description}
        ogImage={video.thumbnail}
        ogType="article"
        articlePublishedTime={video.date}
        articleModifiedTime={video.date}
        articleAuthor={video.author}
        articleTags={video.tags}
        schemaData={schemaData}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name, href: `/category/${video.category}` },
              { label: video.title },
            ]}
          />
        </div>

        <article className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10">
          <div className="flex flex-col lg:flex-row gap-10">
            <div className="flex-1 min-w-0">
              <div className="relative w-full aspect-video rounded-lg overflow-hidden mb-6">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                  <button className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-all duration-300 cursor-pointer group" aria-label="播放视频">
                    <i className="ri-play-fill text-primary-500 text-3xl md:text-4xl group-hover:scale-110 transition-transform w-10 h-10 flex items-center justify-center"></i>
                  </button>
                </div>
                <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2.5 py-1 rounded">
                  {video.duration}
                </div>
                <div className="absolute top-3 left-3 bg-primary-500 text-white text-xs px-2.5 py-1 rounded font-medium">
                  {video.quality}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Link
                  to={`/category/${video.category}`}
                  className="px-3 py-1 bg-primary-50 text-primary-600 text-xs font-medium rounded-full hover:bg-primary-100 transition-colors whitespace-nowrap"
                >
                  {category.name}
                </Link>
                {video.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-secondary-50 text-secondary-600 text-xs font-medium rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground-950 mb-4">
                {video.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 mb-6 pb-6 border-b border-background-200/70">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                  {video.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                  {video.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                  {(video.views / 10000).toFixed(1)}万 次观看
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500 w-4 h-4 flex items-center justify-center"></i>
                  {video.rating} 分
                </span>
              </div>

              <div
                className="prose prose-sm max-w-none text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: video.content }}
              />
            </div>

            <aside className="lg:w-80 flex-shrink-0">
              <div className="sticky top-24">
                <h3 className="text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                  <i className="ri-links-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
                  相关推荐
                </h3>
                <div className="space-y-4">
                  {related.map((v) => (
                    <Link
                      key={v.id}
                      to={`/article/${v.id}`}
                      className="flex gap-3 group no-underline"
                    >
                      <div className="w-28 h-20 rounded-md overflow-hidden flex-shrink-0">
                        <img
                          src={v.thumbnail}
                          alt={v.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-xs font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-500 transition-colors mb-1">
                          {v.title}
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-foreground-500">
                          <span>{v.duration}</span>
                          <span className="flex items-center gap-0.5">
                            <i className="ri-star-fill text-accent-500 text-xs w-3 h-3 flex items-center justify-center"></i>
                            {v.rating}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}